/**
 * CheckoutPage — wraps CheckoutWizard component
 * Route: /checkout
 *
 * G9: Gated by authentication + 'order' permission.
 *     Guests see a sign-in prompt; users without order permission
 *     are redirected to the cart.
 */
import { Link } from 'react-router-dom';
import Breadcrumbs from '@components/layout/Breadcrumbs';
import CheckoutWizard from '@components/commerce/CheckoutWizard';
import useAuth from '@hooks/useAuth';

export default function CheckoutPage() {
  const { isGuest, hasPermission } = useAuth();
  const canOrder = hasPermission('order');

  // G9: Guest gate
  if (isGuest) {
    return (
      <div className="sandvik-container py-6">
        <Breadcrumbs items={[
          { label: 'Home', href: '/' },
          { label: 'Cart', href: '/cart' },
          { label: 'Checkout' },
        ]} />
        <div className="border border-n2 p-10 text-center">
          <span className="material-symbols-outlined text-5xl text-n3 mb-3 block">lock</span>
          <h2 className="font-sandvik-headline font-normal text-xl text-carbon-black mb-2">
            Sign in to checkout
          </h2>
          <p className="text-n5 font-sandvik-text mb-4 text-sm">
            You need to be signed in to place orders.
          </p>
          <Link
            to="/login"
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-electric-blue text-white text-sm hover:bg-eb-3 transition-colors"
          >
            <span className="material-symbols-outlined text-base">login</span>
            Sign in
          </Link>
        </div>
      </div>
    );
  }

  // G9: Permission gate — user is authenticated but lacks order permission
  if (!canOrder) {
    return (
      <div className="sandvik-container py-6">
        <Breadcrumbs items={[
          { label: 'Home', href: '/' },
          { label: 'Cart', href: '/cart' },
          { label: 'Checkout' },
        ]} />
        <div className="border border-n2 p-10 text-center">
          <span className="material-symbols-outlined text-5xl text-n3 mb-3 block">block</span>
          <h2 className="font-sandvik-headline font-normal text-xl text-carbon-black mb-2">
            Ordering not available
          </h2>
          <p className="text-n5 font-sandvik-text mb-4 text-sm">
            Your account does not have permission to place orders. Contact your administrator for access.
          </p>
          <Link
            to="/cart"
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-electric-blue text-white text-sm hover:bg-eb-3 transition-colors"
          >
            <span className="material-symbols-outlined text-base">arrow_back</span>
            Back to cart
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="sandvik-container py-6">
      <Breadcrumbs items={[
        { label: 'Home', href: '/' },
        { label: 'Cart', href: '/cart' },
        { label: 'Checkout' },
      ]} />
      <CheckoutWizard />
    </div>
  );
}
